/**
 * DraftEditToolbar — ScheduleDraft 本地编辑状态工具条。
 *
 * 展示 dirty 状态 badge、保存按钮、Undo/Redo 按钮。位于 Timetable 区块顶部
 * 或作为独立工具条嵌入工作台。仅在有本地编辑能力时渲染。
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md §8.3
 * 骨架关联 → hooks/useLocalEditState.js (dirty 数据源),
 *             hooks/useSaveLocalEdits.js (保存流程),
 *             hooks/useEditGuard.js (快捷键)
 */

import React from "react";
import { Badge, Button, Space, Tooltip, Typography } from "antd";
import { RedoOutlined, SaveOutlined, UndoOutlined } from "@ant-design/icons";

const { Text } = Typography;

/**
 * @typedef {Object} DraftEditToolbarProps
 * @property {Object} dirtyState dirty 状态对象。
 * @property {number} undoStackSize Undo 栈大小。
 * @property {number} redoStackSize Redo 栈大小。
 * @property {boolean} saving 是否正在保存。
 * @property {() => void} onSave 保存回调。
 * @property {() => void} onUndo Undo 回调。
 * @property {() => void} onRedo Redo 回调。
 * @property {boolean} disabled 是否禁用编辑操作。
 */

const DraftEditToolbar = ({
    dirtyState,
    compareDirtyState,
    undoStackSize = 0,
    redoStackSize = 0,
    saving = false,
    onSave,
    onUndo,
    onRedo,
    disabled = false,
    className = "",
}) => {
    const isDirty = dirtyState?.isDirty || compareDirtyState?.isDirty || false;
    const dirtyCount = dirtyState?.dirtyFlightIds?.size || 0;
    const compareDirtyCount = compareDirtyState?.dirtyPairCount || compareDirtyState?.dirtyPairKeys?.size || 0;
    const canUndo = undoStackSize > 0;
    const canRedo = redoStackSize > 0;
    const dirtyLabel = [
        dirtyCount ? `${dirtyCount} 个航班` : null,
        compareDirtyCount ? `${compareDirtyCount} 组匹配` : null,
    ].filter(Boolean).join("、");

    const toolbarClassName = ["schedule-draft-edit-toolbar", className].filter(Boolean).join(" ");

    return (
        <div className={toolbarClassName}>
            <Space size={8}>
                <Tooltip title="撤销 (Ctrl+Z)">
                    <Button
                        size="small"
                        icon={<UndoOutlined />}
                        disabled={disabled || !canUndo}
                        onClick={onUndo}
                    />
                </Tooltip>
                <Tooltip title="重做 (Ctrl+Shift+Z)">
                    <Button
                        size="small"
                        icon={<RedoOutlined />}
                        disabled={disabled || !canRedo}
                        onClick={onRedo}
                    />
                </Tooltip>

                {isDirty && (
                    <Badge
                        count={dirtyCount + compareDirtyCount}
                        size="small"
                        offset={[4, 0]}
                        style={{ backgroundColor: "#faad14" }}
                    >
                        <Text type="warning" className="schedule-draft-edit-toolbar-dirty-label">
                            {dirtyLabel || "本地工作态"} 已修改
                        </Text>
                    </Badge>
                )}

                <Tooltip title={disabled ? "Agent 执行期间编辑已锁定" : isDirty ? "保存所有修改 (Ctrl+S)" : "没有未保存的修改"}>
                    <Button
                        type={isDirty ? "primary" : "default"}
                        size="small"
                        icon={<SaveOutlined />}
                        loading={saving}
                        disabled={disabled || !isDirty || saving}
                        onClick={onSave}
                    >
                        保存
                    </Button>
                </Tooltip>
            </Space>
        </div>
    );
};

export default DraftEditToolbar;
